import type { Backend } from '@freelancer/datastore/core';
import {
  getQueryParamValue,
  OrderByDirection,
} from '@freelancer/datastore/core';
import { assert } from '@freelancer/utils';
import type { QuotationItemRevisionsCollection } from './quotation-item-revisions.types';

function isFieldUpdated(
  oldValue?: number | string,
  newValue?: number | string,
): boolean {
  return !!newValue && newValue !== oldValue;
}

export function quotationItemRevisionsBackend(): Backend<QuotationItemRevisionsCollection> {
  return {
    defaultOrder: {
      field: 'order',
      direction: OrderByDirection.ASC,
    },
    fetch: (authUid, ids, query, order) => {
      const quotationItemId = getQueryParamValue(query, 'quotationItemId')[0];
      const quotationId = getQueryParamValue(query, 'quotationId')[0];

      // On the frontend, we are merging quotation item & quotation item
      // revision into one collection. Quotation item is essentially one
      // version in the revisions (usually the latest version). When
      // quotation item id is not provided, which is required for fetching
      // quotation item revisions, we just fetch quotation items.
      if (!quotationItemId) {
        assert(
          !!quotationId,
          'Quotation Id is required to fetch quotation items.',
        );
        return {
          endpoint: `quotations/0.1/quotations/${quotationId}/items`,
          params: {
            quotation_version_id: getQueryParamValue(
              query,
              'quotationVersionId',
            )[0],
            statuses: getQueryParamValue(query, 'status'),
            offset: query?.searchQueryParams.offset,
            limit: query?.searchQueryParams.limit,
          },
        };
      }

      assert(
        !!quotationId,
        'Missing quotation id, which is required to fetch quotation item revisions.',
      );

      const beforeAndAfterTime = getQueryParamValue(query, 'createDate');
      const time1 = beforeAndAfterTime[0];
      const time2 = beforeAndAfterTime[1];

      return {
        endpoint: `quotations/0.1/quotations/${quotationId}/items/${quotationItemId}/revisions`,
        params: {
          order: getQueryParamValue(query, 'order')[0],
          ...(time1 && time2
            ? {
                after_date: Math.min(time1, time2) / 1000,
                before_date: Math.max(time1, time2) / 1000,
              }
            : {}),
        },
      };
    },
    push: (_, quotationItemRevision, extra) => {
      if (quotationItemRevision.quotationItemId) {
        // When quotation item id is given, we are creating
        // a new version of an existing quotation item.
        assert(
          extra?.previousItemVersionId &&
            typeof extra.previousItemVersionId === 'number',
          `Must provide extra previous_item_version_id as a number when creating a new item version`,
        );
        return {
          endpoint: `quotations/0.1/quotations/${quotationItemRevision.quotationId}/items/${quotationItemRevision.quotationItemId}/revisions`,
          payload: {
            item_id: quotationItemRevision.quotationItemId,
            quotation_id: quotationItemRevision.quotationId,
            name: quotationItemRevision.name,
            order: quotationItemRevision.order,
            unit_price: quotationItemRevision.unitPrice,
            unit_type: quotationItemRevision.unitType,
            quantity: quotationItemRevision.quantity,
            description: quotationItemRevision.description,
            message: quotationItemRevision.message,
            unit_limit: quotationItemRevision.unitLimit,
            previous_item_version_id: extra.previousItemVersionId,
          },
          extractId: result => result.quotation_item.item_version_id,
        };
      }

      // When quotation item id is 0 (invalid), we are
      // creating a brand new quotation item, a base
      // quotation item object and the first version
      // of that quotation item.
      return {
        endpoint: `quotations/0.1/quotations/${quotationItemRevision.quotationId}/items`,
        payload: {
          name: quotationItemRevision.name,
          order: quotationItemRevision.order,
          currency_id: quotationItemRevision.currencyId,
          unit_price: quotationItemRevision.unitPrice,
          quantity: quotationItemRevision.quantity,
          description: quotationItemRevision.description,
          unit_limit: quotationItemRevision.unitLimit,
          unit_type: quotationItemRevision.unitType,
          billing_frequency: quotationItemRevision.billingFrequency,
        },
        extractId: result => result.quotation_item.item_version_id,
      };
    },
    set: undefined,
    update: (authUid, updatedItem, originalItem) => {
      const idTypes = ['id', 'quotationItemId', 'quotationId'] as const;
      idTypes.forEach(i =>
        assert(
          !updatedItem[i] || updatedItem[i] === originalItem[i],
          'Ids fields are immutable',
        ),
      );

      let result;
      if (updatedItem.paidAmount !== undefined) {
        // note: not currently supporting partial payments, allow gaf to elect to pay the full amount on it's own for now.
        result = {
          endpoint: `quotations/0.1/quotations/${originalItem.quotationId}/items/${originalItem.quotationItemId}/release`,
          payload: { item_version_id: originalItem.id },
        };
      } else {
        // Declare update fields array and index with 'number' to obtain a union type of allowed fields
        const itemUpdateFields = [
          'name',
          'order',
          'unitPrice',
          'unitLimit',
          'unitType',
          'description',
          'message',
          'status',
          'billingFrequency',
        ] as const;
        type ItemUpdateField = (typeof itemUpdateFields)[number];

        const getPayloadPropertyName = (field: ItemUpdateField): string =>
          field === 'unitPrice'
            ? 'unit_price'
            : field === 'unitLimit'
              ? 'unit_limit'
              : field === 'billingFrequency'
                ? 'billing_frequency'
                : field;

        // If field is updated, add to payload
        const payload = itemUpdateFields.reduce(
          (acc, field) => ({
            ...acc,
            ...(isFieldUpdated(originalItem[field], updatedItem[field]) && {
              [getPayloadPropertyName(field)]: updatedItem[field],
            }),
          }),
          {},
        );

        assert(
          Object.keys(payload).length > 0,
          'Empty payload. No fields in quotation item need update',
        );

        result = {
          endpoint: `quotations/0.1/quotations/${originalItem.quotationId}/items/${originalItem.quotationItemId}/revisions/${originalItem.id}`,
          payload,
        };
      }
      return {
        method: 'PUT',
        ...result,
      };
    },
    remove: (authUid, id, originDocument) => ({
      method: 'DELETE',
      endpoint: `quotations/0.1/quotations/${originDocument.quotationId}/items/${originDocument.quotationItemId}/`,
      payload: {},
    }),
  };
}
